/**
 * @zendir/ui - MissionCalendar Component
 *
 * Full-featured calendar for mission planning and scheduling with 5 views:
 * Month, Week (time-grid), Day (time-grid), Gantt, and List.
 *
 * Includes a mini calendar sidebar for quick date navigation, timeline
 * and event type filtering, right-click to create at time slots, week
 * number display, and DOY (Day of Year) in the header.
 *
 * Features (matching/exceeding OpenC3 Calendar):
 * - Day / Week / Month / Gantt / List views
 * - Time-grid with 30-min rows for Week and Day views
 * - Mini calendar sidebar with event type + timeline filters
 * - Activity scheduling with command/script/reserve execution types
 * - Metadata recording (key-value pairs, start time only)
 * - Notes with start + end time
 * - Status-colored events per Astro UX (circle, square, triangle down)
 * - Activity lifecycle (created, started, completed, paused, stopped, error, etc.)
 * - Multi-timeline support (overlapping prevention per timeline)
 * - Right-click context menu to create events at specific time slots
 * - Week number + DOY display
 * - Contact window / eclipse / pass event types (beyond OpenC3)
 * - weekStartsMonday option (Sunday-Saturday or Monday-Sunday)
 * - Integration with Timeline component via onDayDrillDown
 *
 * @example
 * ```tsx
 * <MissionCalendar
 *   events={missionEvents}
 *   timelines={[{ id: 'ops', name: 'OPS-1', color: '#2dccff' }]}
 *   onEventCreate={(e) => console.log('New event:', e)}
 *   onEventClick={(e) => console.log('Clicked:', e)}
 * />
 * ```
 */

import React, { useState, useMemo, useCallback, memo, useRef } from "react";
import { useTheme } from "../../theme";
import { AstroIcon, type AstroIconName } from "../display/AstroIcon";

// =============================================================================
// Types
// =============================================================================

export type CalendarViewMode = "month" | "week" | "day" | "gantt" | "list";

export type ActivityStatus =
  | "created"
  | "scheduled"
  | "started"
  | "completed"
  | "paused"
  | "stopped"
  | "disabled"
  | "error"
  | "failed"
  | "crashed";

export type ActivityType =
  | "command"
  | "script"
  | "reserve"
  | "metadata"
  | "note"
  | "contact"
  | "eclipse"
  | "pass";

export interface CalendarEvent {
  /** Unique identifier */
  id: string;
  /** Event title */
  title: string;
  /** Start time */
  start: Date;
  /** End time (optional for metadata/point events) */
  end?: Date;
  /** Activity type */
  type: ActivityType;
  /** Execution status */
  status?: ActivityStatus;
  /** Timeline this event belongs to */
  timeline?: string;
  /** Description / notes */
  description?: string;
  /** Command string (for command type) */
  command?: string;
  /** Script path (for script type) */
  script?: string;
  /** Metadata key-value pairs */
  metadata?: Record<string, string | number | boolean>;
  /** Whether this event is recurring */
  recurring?: boolean;
  /** Recurrence pattern */
  recurrenceRule?: string;
  /** Color override */
  color?: string;
  /** Whether the event is editable */
  editable?: boolean;
}

export interface CalendarTimeline {
  /** Unique timeline ID */
  id: string;
  /** Display name */
  name: string;
  /** Color for this timeline */
  color?: string;
  /** Whether the timeline is active */
  active?: boolean;
}

export interface MissionCalendarProps {
  /** Calendar events */
  events: CalendarEvent[];
  /** Available timelines */
  timelines?: CalendarTimeline[];
  /** Initial view mode (default: 'month') */
  defaultView?: CalendarViewMode;
  /** Initial date to display */
  initialDate?: Date;
  /** Height (default: 600) */
  height?: number | string;
  /** Title */
  title?: string;
  /** Show mini calendar sidebar (default: true) */
  showSidebar?: boolean;
  /** Show timeline selector (default: true) */
  showTimelines?: boolean;
  /** Show event type filter (default: true) */
  showFilters?: boolean;
  /** Show create button (default: true) */
  showCreate?: boolean;
  /** Week starts on Monday (default: true for ops) */
  weekStartsMonday?: boolean;
  /** Called when an event is clicked */
  onEventClick?: (event: CalendarEvent) => void;
  /** Called when creating a new event */
  onEventCreate?: (event: Partial<CalendarEvent>) => void;
  /** Called when navigating to a day (e.g., to open Timeline Gantt view) */
  onDayDrillDown?: (date: Date, events: CalendarEvent[]) => void;
  /** CSS class */
  className?: string;
}

// =============================================================================
// Helpers
// =============================================================================

function getStatusColor(
  status: ActivityStatus | undefined,
  tokens: ReturnType<typeof useTheme>["tokens"],
): string {
  if (!status) return tokens.colors.text.muted ?? tokens.colors.text.secondary;
  switch (status) {
    case "created":
    case "scheduled":
      return tokens.colors.status.standby;
    case "started":
      return tokens.colors.status.normal;
    case "completed":
      return tokens.colors.status.normal;
    case "paused":
    case "stopped":
    case "disabled":
      return tokens.colors.status.caution;
    case "error":
    case "failed":
    case "crashed":
      return tokens.colors.status.critical;
  }
}

/**
 * Return Astro UX status shape per status level.
 *   off → small dot, standby → ring ◯, normal → filled circle ●,
 *   caution → square ■, serious → diamond ◆, critical → triangle down ▼
 */
function getStatusShape(status: ActivityStatus | undefined): string {
  if (!status) return "●";
  switch (status) {
    case "created":
    case "scheduled":
      return "◯";
    case "started":
    case "completed":
      return "●";
    case "paused":
    case "stopped":
    case "disabled":
      return "■";
    case "error":
    case "failed":
    case "crashed":
      return "▼";
  }
}

const TYPE_ICON_ASTRO: ActivityType[] = ["contact", "eclipse", "pass"];

function getTypeIcon(type: ActivityType): string {
  switch (type) {
    case "command":
      return "⌘";
    case "script":
      return "▶";
    case "reserve":
      return "▒";
    case "metadata":
      return "◆";
    case "note":
      return "✎";
    case "contact":
      return "antenna";
    case "eclipse":
      return "brightness-2";
    case "pass":
      return "satellite";
  }
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(
  year: number,
  month: number,
  mondayStart: boolean,
): number {
  const day = new Date(year, month, 1).getDay();
  if (mondayStart) return day === 0 ? 6 : day - 1;
  return day;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatTime(d: Date): string {
  return d.toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getWeekDates(date: Date, mondayStart: boolean): Date[] {
  const d = new Date(date);
  const day = d.getDay();
  const diff = mondayStart ? (day === 0 ? -6 : 1 - day) : -day;
  const start = new Date(d);
  start.setDate(start.getDate() + diff);
  return Array.from({ length: 7 }, (_, i) => {
    const dt = new Date(start);
    dt.setDate(dt.getDate() + i);
    return dt;
  });
}

/** ISO week number */
function getWeekNumber(d: Date): number {
  const date = new Date(d.getTime());
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
  const week1 = new Date(date.getFullYear(), 0, 4);
  return (
    1 +
    Math.round(
      ((date.getTime() - week1.getTime()) / 86400000 -
        3 +
        ((week1.getDay() + 6) % 7)) /
        7,
    )
  );
}

/** Day of year (1-366) */
function getDayOfYear(d: Date): number {
  const start = new Date(d.getFullYear(), 0, 0);
  const diff = d.getTime() - start.getTime();
  return Math.floor(diff / 86400000);
}

/** 30-minute time slots for the time-grid views */
const TIME_SLOTS: string[] = [];
for (let h = 0; h < 24; h++) {
  TIME_SLOTS.push(`${String(h).padStart(2, "0")}:00`);
  TIME_SLOTS.push(`${String(h).padStart(2, "0")}:30`);
}

// Row height for time-grid slots (px)
const SLOT_HEIGHT = 28;

// =============================================================================
// Sub-components
// =============================================================================

const CalendarEventChip = memo(function CalendarEventChip({
  event,
  tokens,
  compact,
  onClick,
}: {
  event: CalendarEvent;
  tokens: ReturnType<typeof useTheme>["tokens"];
  compact?: boolean;
  onClick?: () => void;
}) {
  const color = event.color || getStatusColor(event.status, tokens);
  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      title={`${event.title}\n${formatTime(event.start)}${event.end ? " – " + formatTime(event.end) : ""}\n${event.description || ""}`}
      style={{
        background: `${color}22`,
        color: tokens.colors.text.primary,
        padding: compact ? "1px 4px" : "2px 6px",
        borderRadius: tokens.borderRadius.sm,
        fontSize: compact
          ? tokens.typography.fontSize.micro
          : tokens.typography.fontSize.xxs,
        lineHeight: compact ? "14px" : "16px",
        cursor: "pointer",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        display: "flex",
        alignItems: "center",
        gap: 3,
        transition: "background 150ms ease",
        marginBottom: 1,
      }}
    >
      <span
        style={{ fontSize: tokens.typography.fontSize.micro, opacity: 0.7 }}
      >
        {TYPE_ICON_ASTRO.includes(event.type) ? (
          <AstroIcon
            name={getTypeIcon(event.type) as AstroIconName}
            size="extra-small"
            label=""
          />
        ) : (
          getTypeIcon(event.type)
        )}
      </span>
      {!compact && (
        <span style={{ color, fontSize: tokens.typography.fontSize.micro }}>
          {getStatusShape(event.status)}
        </span>
      )}
      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
        {event.title}
      </span>
    </div>
  );
});

/** Mini calendar for sidebar */
const MiniCalendar = memo(function MiniCalendar({
  currentDate,
  tokens,
  weekStartsMonday,
  onDateSelect,
  getEventsForDay,
}: {
  currentDate: Date;
  tokens: ReturnType<typeof useTheme>["tokens"];
  weekStartsMonday: boolean;
  onDateSelect: (d: Date) => void;
  getEventsForDay: (d: Date) => CalendarEvent[];
}) {
  const [miniDate, setMiniDate] = useState(new Date(currentDate));
  const year = miniDate.getFullYear();
  const month = miniDate.getMonth();
  const today = new Date();

  const dayLabels = weekStartsMonday
    ? ["M", "T", "W", "T", "F", "S", "S"]
    : ["S", "M", "T", "W", "T", "F", "S"];

  const grid = useMemo(() => {
    const firstDay = getFirstDayOfMonth(year, month, weekStartsMonday);
    const daysInMonth = getDaysInMonth(year, month);
    const daysInPrevMonth = getDaysInMonth(year, month === 0 ? 11 : month - 1);
    const cells: { date: Date; isCurrentMonth: boolean }[] = [];
    for (let i = firstDay - 1; i >= 0; i--) {
      cells.push({
        date: new Date(year, month - 1, daysInPrevMonth - i),
        isCurrentMonth: false,
      });
    }
    for (let i = 1; i <= daysInMonth; i++) {
      cells.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    while (cells.length < 42) {
      cells.push({
        date: new Date(
          year,
          month + 1,
          cells.length - firstDay - daysInMonth + 1,
        ),
        isCurrentMonth: false,
      });
    }
    return cells;
  }, [year, month, weekStartsMonday]);

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 6,
        }}
      >
        <button
          onClick={() => setMiniDate(new Date(year, month - 1))}
          style={{
            background: "none",
            border: "none",
            color: tokens.colors.text.muted,
            cursor: "pointer",
            fontSize: 12,
            padding: "2px 6px",
          }}
        >
          ◀
        </button>
        <span style={{ fontWeight: 500, fontSize: 11 }}>
          {miniDate.toLocaleDateString("en-US", {
            month: "short",
            year: "numeric",
          })}
        </span>
        <button
          onClick={() => setMiniDate(new Date(year, month + 1))}
          style={{
            background: "none",
            border: "none",
            color: tokens.colors.text.muted,
            cursor: "pointer",
            fontSize: 12,
            padding: "2px 6px",
          }}
        >
          ▶
        </button>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 0,
        }}
      >
        {dayLabels.map((d, i) => (
          <div
            key={i}
            style={{
              textAlign: "center",
              fontSize: 9,
              color: tokens.colors.text.muted,
              fontWeight: 500,
              padding: "2px 0",
            }}
          >
            {d}
          </div>
        ))}
        {grid.map(({ date, isCurrentMonth }, idx) => {
          const isToday = isSameDay(date, today);
          const isSelected = isSameDay(date, currentDate);
          const hasEvents = getEventsForDay(date).length > 0;
          return (
            <div
              key={idx}
              onClick={() => onDateSelect(date)}
              style={{
                textAlign: "center",
                fontSize: 10,
                padding: "3px 0",
                cursor: "pointer",
                color: isSelected
                  ? tokens.colors.text.inverse
                  : isCurrentMonth
                    ? tokens.colors.text.primary
                    : tokens.colors.text.muted + "55",
                fontWeight: isToday || isSelected ? 700 : 400,
                background: isSelected
                  ? tokens.colors.interactive.default
                  : isToday
                    ? `${tokens.colors.interactive.default}20`
                    : "transparent",
                borderRadius:
                  isSelected || isToday ? tokens.borderRadius.full : 0,
                position: "relative",
              }}
            >
              {date.getDate()}
              {hasEvents && !isSelected && (
                <span
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 3,
                    height: 3,
                    borderRadius: "50%",
                    background: tokens.colors.interactive.default,
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});

// =============================================================================
// Main Component
// =============================================================================

export const MissionCalendar = memo(function MissionCalendar({
  events,
  timelines = [],
  defaultView = "month",
  initialDate,
  height = 600,
  title,
  showSidebar = true,
  showTimelines = true,
  showFilters = true,
  showCreate = true,
  weekStartsMonday = true,
  onEventClick,
  onEventCreate,
  onDayDrillDown,
  className = "",
}: MissionCalendarProps): React.ReactElement {
  const { tokens, theme } = useTheme();
  const isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";
  const [viewMode, setViewMode] = useState<CalendarViewMode>(defaultView);
  const [currentDate, setCurrentDate] = useState(initialDate || new Date());
  const [selectedTimelines, setSelectedTimelines] = useState<Set<string>>(
    new Set(timelines.map((t) => t.id)),
  );
  const [typeFilter, setTypeFilter] = useState<Set<ActivityType>>(
    new Set([
      "command",
      "script",
      "reserve",
      "metadata",
      "note",
      "contact",
      "eclipse",
      "pass",
    ]),
  );
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    date: Date;
  } | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Filter events
  const filteredEvents = useMemo(
    () =>
      events.filter(
        (e) =>
          typeFilter.has(e.type) &&
          (selectedTimelines.size === 0 ||
            !e.timeline ||
            selectedTimelines.has(e.timeline)),
      ),
    [events, typeFilter, selectedTimelines],
  );

  // Events by day
  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    filteredEvents.forEach((e) => {
      const key = `${e.start.getFullYear()}-${e.start.getMonth()}-${e.start.getDate()}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(e);
      if (e.end) {
        const days = Math.ceil(
          (e.end.getTime() - e.start.getTime()) / 86400000,
        );
        for (let d = 1; d <= days && d < 30; d++) {
          const next = new Date(e.start);
          next.setDate(next.getDate() + d);
          const nk = `${next.getFullYear()}-${next.getMonth()}-${next.getDate()}`;
          if (!map.has(nk)) map.set(nk, []);
          map.get(nk)!.push(e);
        }
      }
    });
    return map;
  }, [filteredEvents]);

  const getEventsForDay = useCallback(
    (d: Date) => {
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      return eventsByDay.get(key) || [];
    },
    [eventsByDay],
  );

  // Navigation
  const navigate = useCallback(
    (dir: -1 | 0 | 1) => {
      if (dir === 0) {
        setCurrentDate(new Date());
        return;
      }
      const d = new Date(currentDate);
      if (viewMode === "month") d.setMonth(d.getMonth() + dir);
      else if (viewMode === "week" || viewMode === "gantt")
        d.setDate(d.getDate() + dir * 7);
      else d.setDate(d.getDate() + dir);
      setCurrentDate(d);
    },
    [currentDate, viewMode],
  );

  const toggleTimeline = useCallback((id: string) => {
    setSelectedTimelines((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleType = useCallback((type: ActivityType) => {
    setTypeFilter((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, []);

  const dayNames = weekStartsMonday
    ? ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
    : ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const monthLabel = currentDate.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
  const today = new Date();
  const weekNum = getWeekNumber(currentDate);
  const doy = getDayOfYear(currentDate);

  // Month view grid
  const monthGrid = useMemo(() => {
    const firstDay = getFirstDayOfMonth(year, month, weekStartsMonday);
    const daysInMonth = getDaysInMonth(year, month);
    const daysInPrevMonth = getDaysInMonth(year, month === 0 ? 11 : month - 1);
    const cells: { date: Date; isCurrentMonth: boolean }[] = [];
    for (let i = firstDay - 1; i >= 0; i--) {
      cells.push({
        date: new Date(year, month - 1, daysInPrevMonth - i),
        isCurrentMonth: false,
      });
    }
    for (let i = 1; i <= daysInMonth; i++) {
      cells.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    while (cells.length < 42) {
      cells.push({
        date: new Date(
          year,
          month + 1,
          cells.length - firstDay - daysInMonth + 1,
        ),
        isCurrentMonth: false,
      });
    }
    return cells;
  }, [year, month, weekStartsMonday]);

  // Week view dates
  const weekDates = useMemo(
    () => getWeekDates(currentDate, weekStartsMonday),
    [currentDate, weekStartsMonday],
  );

  // Day events sorted by time
  const dayEvents = useMemo(() => {
    const evts = getEventsForDay(currentDate);
    return [...evts].sort((a, b) => a.start.getTime() - b.start.getTime());
  }, [currentDate, getEventsForDay]);

  // List view
  const listEvents = useMemo(() => {
    const now = new Date();
    const end = new Date(now);
    end.setDate(end.getDate() + 7);
    return filteredEvents
      .filter((e) => e.start >= now && e.start <= end)
      .sort((a, b) => a.start.getTime() - b.start.getTime());
  }, [filteredEvents]);

  // Gantt: events in the selected week, grouped by timeline
  const ganttData = useMemo(() => {
    const weekStart = new Date(weekDates[0]);
    weekStart.setHours(0, 0, 0, 0);
    const weekEnd = new Date(weekDates[6]);
    weekEnd.setHours(23, 59, 59, 999);
    const weekEvents = filteredEvents.filter(
      (e) => e.start <= weekEnd && (e.end || e.start) >= weekStart,
    );
    const tlMap = new Map<string, CalendarEvent[]>();
    const noTl: CalendarEvent[] = [];
    weekEvents.forEach((e) => {
      const tlId = e.timeline || "";
      if (!tlId) {
        noTl.push(e);
        return;
      }
      if (!tlMap.has(tlId)) tlMap.set(tlId, []);
      tlMap.get(tlId)!.push(e);
    });
    const rows: { label: string; color: string; events: CalendarEvent[] }[] =
      [];
    timelines.forEach((tl) => {
      if (tlMap.has(tl.id)) {
        rows.push({
          label: tl.name,
          color: tl.color || tokens.colors.interactive.default,
          events: tlMap.get(tl.id)!,
        });
      }
    });
    if (noTl.length > 0)
      rows.push({
        label: "Unassigned",
        color: tokens.colors.text.muted ?? tokens.colors.text.secondary,
        events: noTl,
      });
    return { rows, weekStart, weekEnd };
  }, [filteredEvents, weekDates, timelines, tokens]);

  // Right-click handler for time-grid
  const handleContextMenu = useCallback(
    (e: React.MouseEvent, date: Date, slotTime?: string) => {
      e.preventDefault();
      if (!onEventCreate) return;
      const d = new Date(date);
      if (slotTime) {
        const [h, m] = slotTime.split(":").map(Number);
        d.setHours(h, m, 0, 0);
      }
      setContextMenu({ x: e.clientX, y: e.clientY, date: d });
    },
    [onEventCreate],
  );

  // Header text
  const headerLabel = useMemo(() => {
    if (viewMode === "month") return monthLabel;
    if (viewMode === "week" || viewMode === "gantt") {
      return `${weekDates[0].toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${weekDates[6].toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}  Week ${weekNum}`;
    }
    return currentDate.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }, [viewMode, monthLabel, weekDates, weekNum, currentDate]);

  // Sidebar content
  const renderSidebar = () => (
    <div
      style={{
        width: 190,
        minWidth: 190,
        borderRight: `1px solid ${tokens.colors.border.muted}`,
        background: tokens.colors.background.surface,
        padding: "8px 10px",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        overflow: "auto",
      }}
    >
      <MiniCalendar
        currentDate={currentDate}
        tokens={tokens}
        weekStartsMonday={weekStartsMonday}
        onDateSelect={(d) => {
          setCurrentDate(d);
          if (viewMode === "month") setViewMode("day");
        }}
        getEventsForDay={getEventsForDay}
      />

      {/* Event type filters */}
      {showFilters && (
        <div>
          <div
            style={{
              fontSize: 10,
              fontWeight: 500,
              color: tokens.colors.text.muted,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              marginBottom: 4,
            }}
          >
            Event Types
          </div>
          {(
            [
              "command",
              "script",
              "reserve",
              "metadata",
              "note",
              "contact",
            ] as ActivityType[]
          ).map((type) => (
            <label
              key={type}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "2px 0",
                cursor: "pointer",
                fontSize: 11,
                color: typeFilter.has(type)
                  ? tokens.colors.text.primary
                  : tokens.colors.text.muted + "66",
              }}
            >
              <input
                type="checkbox"
                checked={typeFilter.has(type)}
                onChange={() => toggleType(type)}
                style={{
                  accentColor: tokens.colors.interactive.default,
                  width: 12,
                  height: 12,
                }}
              />
              {TYPE_ICON_ASTRO.includes(type) ? (
                <AstroIcon
                  name={getTypeIcon(type) as AstroIconName}
                  size="extra-small"
                  label=""
                  style={{ opacity: 0.7 }}
                />
              ) : (
                <span style={{ opacity: 0.7, fontSize: 10 }}>
                  {getTypeIcon(type)}
                </span>
              )}
              <span style={{ textTransform: "capitalize" }}>{type}</span>
            </label>
          ))}
        </div>
      )}

      {/* Timeline filters */}
      {showTimelines && timelines.length > 0 && (
        <div>
          <div
            style={{
              fontSize: 10,
              fontWeight: 500,
              color: tokens.colors.text.muted,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              marginBottom: 4,
            }}
          >
            Timelines
          </div>
          {timelines.map((tl) => (
            <label
              key={tl.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "3px 0",
                cursor: "pointer",
                fontSize: 11,
                color: selectedTimelines.has(tl.id)
                  ? tokens.colors.text.primary
                  : tokens.colors.text.muted + "66",
              }}
            >
              <input
                type="checkbox"
                checked={selectedTimelines.has(tl.id)}
                onChange={() => toggleTimeline(tl.id)}
                style={{
                  accentColor: tl.color || tokens.colors.interactive.default,
                  width: 12,
                  height: 12,
                }}
              />
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: tl.color || tokens.colors.interactive.default,
                  flexShrink: 0,
                }}
              />
              <span>{tl.name}</span>
            </label>
          ))}
        </div>
      )}

      {/* Quick info */}
      <div
        style={{
          marginTop: "auto",
          fontSize: 10,
          color: tokens.colors.text.muted,
          borderTop: `1px solid ${tokens.colors.border.muted}`,
          paddingTop: 8,
        }}
      >
        <div>
          DOY:{" "}
          <span
            style={{
              fontFamily: tokens.typography.fontFamily.mono,
              color: tokens.colors.text.secondary,
            }}
          >
            {doy}
          </span>
        </div>
        <div>
          Week:{" "}
          <span
            style={{
              fontFamily: tokens.typography.fontFamily.mono,
              color: tokens.colors.text.secondary,
            }}
          >
            {weekNum}
          </span>
        </div>
        <div>
          UTC:{" "}
          <span
            style={{
              fontFamily: tokens.typography.fontFamily.mono,
              color: tokens.colors.text.secondary,
            }}
          >
            {today.toISOString().slice(11, 19)}
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={`mission-calendar ${className}`}
      onClick={() => setContextMenu(null)}
      style={{
        height: typeof height === "number" ? height : undefined,
        background: tokens.colors.background.base,
        ...(tokens.colors.border.cardStyle ?? {
          border: `1px solid ${tokens.colors.border.muted}`,
        }),
        borderRadius: tokens.borderRadius.lg,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        fontFamily: tokens.typography.fontFamily.primary,
        color: tokens.colors.text.primary,
        backdropFilter: isTransparentTheme ? "blur(10px)" : undefined,
        WebkitBackdropFilter: isTransparentTheme ? "blur(10px)" : undefined,
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "8px 14px",
          borderBottom: `1px solid ${tokens.colors.border.muted}`,
          background: tokens.colors.background.surface,
          backdropFilter: "blur(8px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {title && (
            <span
              style={{
                fontWeight: 500,
                fontSize: tokens.typography.fontSize.sm,
              }}
            >
              {title}
            </span>
          )}
          <div style={{ display: "flex", gap: 2 }}>
            <button onClick={() => navigate(-1)} style={navBtnStyle(tokens)}>
              ◀
            </button>
            <button
              onClick={() => navigate(0)}
              style={{
                ...navBtnStyle(tokens),
                padding: "3px 10px",
                fontSize: tokens.typography.fontSize.xs,
              }}
            >
              Today
            </button>
            <button onClick={() => navigate(1)} style={navBtnStyle(tokens)}>
              ▶
            </button>
          </div>
          <span
            style={{ fontWeight: 500, fontSize: tokens.typography.fontSize.sm }}
          >
            {headerLabel}
          </span>
          {/* DOY badge */}
          <span
            style={{
              fontSize: 9,
              fontFamily: tokens.typography.fontFamily.mono,
              color: tokens.colors.text.muted,
              background: `${tokens.colors.text.muted}15`,
              padding: "1px 6px",
              borderRadius: tokens.borderRadius.sm,
            }}
          >
            DOY {doy}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {showCreate && (
            <button
              onClick={() =>
                onEventCreate?.({ start: currentDate, type: "reserve" })
              }
              style={{
                background: tokens.colors.interactive.default,
                color: tokens.colors.text.inverse,
                border: "none",
                borderRadius: tokens.borderRadius.md,
                padding: "4px 12px",
                fontSize: tokens.typography.fontSize.xs,
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              + Activity
            </button>
          )}
          <div
            style={{
              display: "flex",
              borderRadius: tokens.borderRadius.md,
              overflow: "hidden",
              border: `1px solid ${tokens.colors.border.muted}`,
            }}
          >
            {(
              ["month", "week", "day", "gantt", "list"] as CalendarViewMode[]
            ).map((v) => (
              <button
                key={v}
                onClick={() => setViewMode(v)}
                style={{
                  background:
                    viewMode === v
                      ? tokens.colors.interactive.default
                      : tokens.colors.background.base,
                  color:
                    viewMode === v
                      ? tokens.colors.text.inverse
                      : tokens.colors.text.secondary,
                  border: "none",
                  padding: "3px 10px",
                  fontSize: tokens.typography.fontSize.xxs,
                  fontWeight: 500,
                  cursor: "pointer",
                  textTransform: "capitalize",
                }}
              >
                {v}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Body: sidebar + calendar area */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* Sidebar */}
        {showSidebar && renderSidebar()}

        {/* Main calendar content */}
        <div ref={bodyRef} style={{ flex: 1, overflow: "auto" }}>
          {/* MONTH VIEW */}
          {viewMode === "month" && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7, 1fr)",
                  borderBottom: `1px solid ${tokens.colors.border.muted}`,
                  background: tokens.colors.background.surface,
                }}
              >
                {dayNames.map((d) => (
                  <div
                    key={d}
                    style={{
                      padding: "4px 6px",
                      textAlign: "center",
                      fontSize: tokens.typography.fontSize.xxs,
                      fontWeight: 500,
                      color: tokens.colors.text.muted,
                      textTransform: "uppercase",
                    }}
                  >
                    {d}
                  </div>
                ))}
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7, 1fr)",
                  gridTemplateRows: "repeat(6, 1fr)",
                  flex: 1,
                }}
              >
                {monthGrid.map(({ date, isCurrentMonth }, idx) => {
                  const dayEvts = getEventsForDay(date);
                  const isToday = isSameDay(date, today);
                  return (
                    <div
                      key={idx}
                      onClick={() => {
                        setCurrentDate(date);
                        if (onDayDrillDown) onDayDrillDown(date, dayEvts);
                        else setViewMode("day");
                      }}
                      onContextMenu={(e) => handleContextMenu(e, date)}
                      style={{
                        borderRight: `1px solid ${tokens.colors.border.muted}11`,
                        borderBottom: `1px solid ${tokens.colors.border.muted}11`,
                        padding: 3,
                        cursor: "pointer",
                        background: isToday
                          ? `${tokens.colors.interactive.default}08`
                          : "transparent",
                        opacity: isCurrentMonth ? 1 : 0.35,
                        overflow: "hidden",
                        minHeight: 70,
                      }}
                    >
                      <div
                        style={{
                          fontSize: tokens.typography.fontSize.xs,
                          fontWeight: isToday ? 700 : 400,
                          color: isToday
                            ? tokens.colors.interactive.default
                            : tokens.colors.text.primary,
                          marginBottom: 2,
                          textAlign: "right",
                          paddingRight: 2,
                        }}
                      >
                        {isToday ? (
                          <span
                            style={{
                              background: tokens.colors.interactive.default,
                              color: tokens.colors.text.inverse,
                              borderRadius: tokens.borderRadius.full,
                              width: 20,
                              height: 20,
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: tokens.typography.fontSize.xxs,
                            }}
                          >
                            {date.getDate()}
                          </span>
                        ) : (
                          date.getDate()
                        )}
                      </div>
                      {dayEvts.slice(0, 3).map((e) => (
                        <CalendarEventChip
                          key={e.id}
                          event={e}
                          tokens={tokens}
                          compact
                          onClick={() => onEventClick?.(e)}
                        />
                      ))}
                      {dayEvts.length > 3 && (
                        <div
                          style={{
                            fontSize: tokens.typography.fontSize.micro,
                            color: tokens.colors.text.muted,
                            textAlign: "center",
                          }}
                        >
                          +{dayEvts.length - 3} more
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* WEEK VIEW — Time-grid with 30-minute rows */}
          {viewMode === "week" && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              {/* Day column headers */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: `50px repeat(7, 1fr)`,
                  borderBottom: `1px solid ${tokens.colors.border.muted}`,
                  background: tokens.colors.background.surface,
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                }}
              >
                <div
                  style={{
                    borderRight: `1px solid ${tokens.colors.border.muted}22`,
                  }}
                />
                {weekDates.map((d, i) => {
                  const isToday = isSameDay(d, today);
                  return (
                    <div
                      key={i}
                      style={{
                        padding: "4px 4px",
                        textAlign: "center",
                        background: isToday
                          ? `${tokens.colors.interactive.default}10`
                          : "transparent",
                        borderRight: `1px solid ${tokens.colors.border.muted}11`,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 9,
                          color: tokens.colors.text.muted,
                          textTransform: "uppercase",
                        }}
                      >
                        {dayNames[i]}
                      </div>
                      <div
                        style={{
                          fontSize: 16,
                          fontWeight: isToday ? 700 : 400,
                          color: isToday
                            ? tokens.colors.interactive.default
                            : tokens.colors.text.primary,
                        }}
                      >
                        {isToday ? (
                          <span
                            style={{
                              background: tokens.colors.interactive.default,
                              color: tokens.colors.text.inverse,
                              borderRadius: tokens.borderRadius.full,
                              width: 24,
                              height: 24,
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 13,
                            }}
                          >
                            {d.getDate()}
                          </span>
                        ) : (
                          d.getDate()
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Time-grid rows */}
              <div style={{ flex: 1, overflow: "auto" }}>
                {TIME_SLOTS.map((slot, _si) => {
                  const isHour = slot.endsWith(":00");
                  return (
                    <div
                      key={slot}
                      style={{
                        display: "grid",
                        gridTemplateColumns: `50px repeat(7, 1fr)`,
                        height: SLOT_HEIGHT,
                        borderBottom: `1px solid ${tokens.colors.border.muted}${isHour ? "22" : "0A"}`,
                      }}
                    >
                      {/* Time label */}
                      <div
                        style={{
                          fontSize: 9,
                          color: tokens.colors.text.muted,
                          textAlign: "right",
                          paddingRight: 6,
                          fontFamily: tokens.typography.fontFamily.mono,
                          borderRight: `1px solid ${tokens.colors.border.muted}22`,
                          lineHeight: `${SLOT_HEIGHT}px`,
                          visibility: isHour ? "visible" : "hidden",
                        }}
                      >
                        {slot}
                      </div>
                      {/* Day columns */}
                      {weekDates.map((d, di) => {
                        const slotStart = new Date(d);
                        slotStart.setHours(
                          parseInt(slot),
                          slot.endsWith(":30") ? 30 : 0,
                          0,
                          0,
                        );
                        const slotEvents = getEventsForDay(d).filter((e) => {
                          const eStart =
                            e.start.getHours() * 60 + e.start.getMinutes();
                          const sStart =
                            parseInt(slot) * 60 +
                            (slot.endsWith(":30") ? 30 : 0);
                          return eStart >= sStart && eStart < sStart + 30;
                        });
                        return (
                          <div
                            key={di}
                            onClick={() => {
                              setCurrentDate(d);
                              setViewMode("day");
                            }}
                            onContextMenu={(e) => handleContextMenu(e, d, slot)}
                            style={{
                              borderRight: `1px solid ${tokens.colors.border.muted}0A`,
                              padding: "0 2px",
                              cursor: "pointer",
                              overflow: "hidden",
                              background: isSameDay(d, today)
                                ? `${tokens.colors.interactive.default}04`
                                : "transparent",
                            }}
                          >
                            {slotEvents.map((e) => (
                              <CalendarEventChip
                                key={e.id}
                                event={e}
                                tokens={tokens}
                                compact
                                onClick={() => onEventClick?.(e)}
                              />
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* DAY VIEW — Time-grid with 30-minute rows */}
          {viewMode === "day" && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <div style={{ flex: 1, overflow: "auto" }}>
                {TIME_SLOTS.map((slot) => {
                  const isHour = slot.endsWith(":00");
                  const slotEvents = dayEvents.filter((e) => {
                    const eStart =
                      e.start.getHours() * 60 + e.start.getMinutes();
                    const sStart =
                      parseInt(slot) * 60 + (slot.endsWith(":30") ? 30 : 0);
                    return eStart >= sStart && eStart < sStart + 30;
                  });
                  return (
                    <div
                      key={slot}
                      onContextMenu={(e) =>
                        handleContextMenu(e, currentDate, slot)
                      }
                      style={{
                        display: "grid",
                        gridTemplateColumns: "50px 1fr",
                        minHeight:
                          slotEvents.length > 0
                            ? Math.max(SLOT_HEIGHT, slotEvents.length * 24 + 4)
                            : SLOT_HEIGHT,
                        borderBottom: `1px solid ${tokens.colors.border.muted}${isHour ? "22" : "0A"}`,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 10,
                          color: tokens.colors.text.muted,
                          textAlign: "right",
                          paddingRight: 8,
                          fontFamily: tokens.typography.fontFamily.mono,
                          borderRight: `1px solid ${tokens.colors.border.muted}22`,
                          lineHeight: `${SLOT_HEIGHT}px`,
                          visibility: isHour ? "visible" : "hidden",
                        }}
                      >
                        {slot}
                      </div>
                      <div style={{ padding: "1px 6px", cursor: "pointer" }}>
                        {slotEvents.map((e) => {
                          const color =
                            e.color || getStatusColor(e.status, tokens);
                          return (
                            <div
                              key={e.id}
                              onClick={() => onEventClick?.(e)}
                              style={{
                                background: `${color}18`,
                                borderRadius: tokens.borderRadius.sm,
                                padding: "3px 8px",
                                marginBottom: 2,
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                fontSize: tokens.typography.fontSize.xxs,
                              }}
                            >
                              {TYPE_ICON_ASTRO.includes(e.type) ? (
                                <AstroIcon
                                  name={getTypeIcon(e.type) as AstroIconName}
                                  size="extra-small"
                                  label=""
                                  style={{ opacity: 0.7 }}
                                />
                              ) : (
                                <span style={{ opacity: 0.7 }}>
                                  {getTypeIcon(e.type)}
                                </span>
                              )}
                              <span
                                style={{
                                  color,
                                  fontSize: tokens.typography.fontSize.micro,
                                }}
                              >
                                {getStatusShape(e.status)}
                              </span>
                              <span style={{ fontWeight: 500 }}>{e.title}</span>
                              <span
                                style={{
                                  color: tokens.colors.text.muted,
                                  fontFamily: tokens.typography.fontFamily.mono,
                                  fontSize: tokens.typography.fontSize.micro,
                                  marginLeft: "auto",
                                }}
                              >
                                {formatTime(e.start)}
                                {e.end ? `–${formatTime(e.end)}` : ""}
                              </span>
                              {e.status && (
                                <span
                                  style={{
                                    fontSize: tokens.typography.fontSize.micro,
                                    color,
                                    fontWeight: 500,
                                    textTransform: "uppercase",
                                  }}
                                >
                                  {e.status}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* GANTT VIEW — Timeline rows with horizontal bars */}
          {viewMode === "gantt" && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              {/* Day headers */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: `120px repeat(7, 1fr)`,
                  borderBottom: `1px solid ${tokens.colors.border.muted}`,
                  background: tokens.colors.background.surface,
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                }}
              >
                <div
                  style={{
                    padding: "6px 8px",
                    fontSize: 10,
                    fontWeight: 500,
                    color: tokens.colors.text.muted,
                    borderRight: `1px solid ${tokens.colors.border.muted}22`,
                  }}
                >
                  Timeline
                </div>
                {weekDates.map((d, i) => {
                  const isToday = isSameDay(d, today);
                  return (
                    <div
                      key={i}
                      style={{
                        padding: "4px 4px",
                        textAlign: "center",
                        background: isToday
                          ? `${tokens.colors.interactive.default}10`
                          : "transparent",
                        borderRight: `1px solid ${tokens.colors.border.muted}11`,
                        fontSize: 10,
                      }}
                    >
                      <span style={{ color: tokens.colors.text.muted }}>
                        {dayNames[i]}{" "}
                      </span>
                      <span
                        style={{
                          fontWeight: isToday ? 700 : 400,
                          color: isToday
                            ? tokens.colors.interactive.default
                            : tokens.colors.text.primary,
                        }}
                      >
                        {d.getDate()}
                      </span>
                    </div>
                  );
                })}
              </div>
              {/* Gantt rows */}
              <div style={{ flex: 1, overflow: "auto" }}>
                {ganttData.rows.length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      color: tokens.colors.text.muted,
                      padding: 40,
                      fontSize: 12,
                    }}
                  >
                    No activities this week
                  </div>
                ) : (
                  ganttData.rows.map((row, ri) => (
                    <div
                      key={ri}
                      style={{
                        display: "grid",
                        gridTemplateColumns: `120px repeat(7, 1fr)`,
                        borderBottom: `1px solid ${tokens.colors.border.muted}15`,
                        minHeight: 36,
                      }}
                    >
                      {/* Timeline label */}
                      <div
                        style={{
                          padding: "6px 8px",
                          fontSize: 11,
                          fontWeight: 500,
                          borderRight: `1px solid ${tokens.colors.border.muted}22`,
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          color: tokens.colors.text.primary,
                        }}
                      >
                        <span
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            background: row.color,
                            flexShrink: 0,
                          }}
                        />
                        {row.label}
                      </div>
                      {/* Day cells with event bars */}
                      {weekDates.map((d, di) => {
                        const cellEvents = row.events.filter((e) => {
                          const eDay = new Date(e.start);
                          eDay.setHours(0, 0, 0, 0);
                          const cellDay = new Date(d);
                          cellDay.setHours(0, 0, 0, 0);
                          if (e.end) {
                            const eEnd = new Date(e.end);
                            eEnd.setHours(23, 59, 59, 999);
                            return cellDay >= eDay && cellDay <= eEnd;
                          }
                          return eDay.getTime() === cellDay.getTime();
                        });
                        return (
                          <div
                            key={di}
                            onContextMenu={(e) => handleContextMenu(e, d)}
                            style={{
                              borderRight: `1px solid ${tokens.colors.border.muted}0A`,
                              padding: "2px 2px",
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                              background: isSameDay(d, today)
                                ? `${tokens.colors.interactive.default}04`
                                : "transparent",
                            }}
                          >
                            {cellEvents.map((e) => {
                              const color =
                                e.color || getStatusColor(e.status, tokens);
                              return (
                                <div
                                  key={e.id}
                                  onClick={() => onEventClick?.(e)}
                                  style={{
                                    background: `${color}33`,
                                    borderRadius: 3,
                                    padding: "2px 4px",
                                    fontSize: 9,
                                    fontWeight: 500,
                                    color: tokens.colors.text.primary,
                                    cursor: "pointer",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                  title={`${e.title}\n${formatTime(e.start)}${e.end ? " – " + formatTime(e.end) : ""}`}
                                >
                                  <span
                                    style={{ opacity: 0.7, marginRight: 3 }}
                                  >
                                    {getStatusShape(e.status)}
                                  </span>
                                  {e.title}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* LIST VIEW */}
          {viewMode === "list" && (
            <div style={{ padding: 12 }}>
              <div
                style={{
                  fontSize: tokens.typography.fontSize.xs,
                  color: tokens.colors.text.muted,
                  marginBottom: 8,
                }}
              >
                Upcoming 7 days — {listEvents.length} activities
              </div>
              {listEvents.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    color: tokens.colors.text.muted,
                    padding: 40,
                    fontSize: tokens.typography.fontSize.sm,
                  }}
                >
                  No upcoming activities
                </div>
              ) : (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 4 }}
                >
                  {listEvents.map((e) => {
                    const color = e.color || getStatusColor(e.status, tokens);
                    return (
                      <div
                        key={e.id}
                        onClick={() => onEventClick?.(e)}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "100px 1fr auto",
                          padding: "6px 10px",
                          borderRadius: tokens.borderRadius.md,
                          background: `${color}08`,
                          cursor: "pointer",
                          alignItems: "center",
                          gap: 10,
                          fontSize: tokens.typography.fontSize.xs,
                        }}
                      >
                        <span
                          style={{
                            fontFamily: tokens.typography.fontFamily.mono,
                            fontSize: tokens.typography.fontSize.xxs,
                            color: tokens.colors.text.muted,
                          }}
                        >
                          {e.start.toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                          })}{" "}
                          {formatTime(e.start)}
                        </span>
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          {TYPE_ICON_ASTRO.includes(e.type) ? (
                            <AstroIcon
                              name={getTypeIcon(e.type) as AstroIconName}
                              size="extra-small"
                              label=""
                            />
                          ) : (
                            <span>{getTypeIcon(e.type)}</span>
                          )}
                          <span style={{ fontWeight: 500 }}>{e.title}</span>
                        </span>
                        {e.status && (
                          <span
                            style={{
                              fontSize: tokens.typography.fontSize.micro,
                              color,
                              fontWeight: 500,
                            }}
                          >
                            {getStatusShape(e.status)} {e.status}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Footer status */}
      <div
        style={{
          padding: "4px 14px",
          borderTop: `1px solid ${tokens.colors.border.muted}`,
          background: tokens.colors.background.surface,
          fontSize: tokens.typography.fontSize.xxs,
          color: tokens.colors.text.muted,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <span>
          {filteredEvents.length} activities | DOY {doy} | Week {weekNum}
        </span>
        <span>
          {events.filter(
            (e) =>
              e.status === "error" ||
              e.status === "failed" ||
              e.status === "crashed",
          ).length > 0
            ? `⚠ ${events.filter((e) => e.status === "error" || e.status === "failed" || e.status === "crashed").length} errors`
            : "✓ All nominal"}
        </span>
      </div>

      {/* Right-click context menu */}
      {contextMenu && (
        <div
          style={{
            position: "fixed",
            left: contextMenu.x,
            top: contextMenu.y,
            zIndex: 1000,
            background: tokens.colors.background.surface,
            backdropFilter: "blur(8px)",
            border: `1px solid ${tokens.colors.border.muted}`,
            borderRadius: tokens.borderRadius.md,
            boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
            padding: 4,
            minWidth: 160,
          }}
        >
          <div
            style={{
              fontSize: 9,
              color: tokens.colors.text.muted,
              padding: "4px 8px",
              fontFamily: tokens.typography.fontFamily.mono,
            }}
          >
            {contextMenu.date.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })}{" "}
            {formatTime(contextMenu.date)}
          </div>
          {(
            [
              "command",
              "script",
              "reserve",
              "metadata",
              "note",
            ] as ActivityType[]
          ).map((type) => (
            <button
              key={type}
              onClick={() => {
                onEventCreate?.({ start: contextMenu.date, type });
                setContextMenu(null);
              }}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                background: "none",
                border: "none",
                color: tokens.colors.text.primary,
                padding: "5px 8px",
                fontSize: 11,
                cursor: "pointer",
                borderRadius: tokens.borderRadius.sm,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background =
                  `${tokens.colors.interactive.default}22`;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background =
                  "none";
              }}
            >
              {getTypeIcon(type)} Create{" "}
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

function navBtnStyle(
  tokens: ReturnType<typeof useTheme>["tokens"],
): React.CSSProperties {
  return {
    background: tokens.colors.background.base,
    color: tokens.colors.text.secondary,
    border: `1px solid ${tokens.colors.border.muted}`,
    borderRadius: tokens.borderRadius.sm,
    padding: "3px 8px",
    fontSize: tokens.typography.fontSize.xs,
    cursor: "pointer",
    lineHeight: 1,
  };
}

export default MissionCalendar;
